/**
 * Sprachnachrichten aufnehmen — und warum das Format der wunde Punkt war.
 *
 * **Der Server kann nicht umwandeln.** Kein ffmpeg auf dem Paket; was
 * hereinkommt, wird abgelegt und genau so wieder ausgeliefert. Damit
 * entscheidet der aufnehmende Browser, wer es später abspielen kann:
 *
 * - **MP4/AAC** spielt jeder — Safari, Chrome, Firefox, Windows. Safari
 *   nimmt ausschließlich das auf.
 * - **WebM/Opus** nimmt Chrome auf, wenn es MP4 nicht kann. **Safari spielt
 *   es nicht ab.** Das ist dasselbe Loch wie bei den Videos.
 * - **Ogg/Opus** nimmt Firefox auf; Safari spielt es ebenfalls nicht.
 *
 * Deshalb wird **MP4 zuerst erfragt** und erst danach der Rest.
 *
 * **Und wo trotzdem WebM oder Ogg herauskommt, rechnet der Browser um**
 * (`ueberallAbspielbar()`, `tonwandeln.ts`). Der Server kann es nicht, aber
 * er muss auch nicht: eine Sprachnachricht ist klein genug, um sie dort zu
 * wandeln, wo sie entsteht. Ein Video wäre es nicht — dort bleibt das Loch.
 *
 * Hier stand vorher, die Oberfläche sage es dem Aufnehmenden eben. Das war
 * richtig und trotzdem zu wenig: getroffen hätte es den Mitlesenden an einem
 * Android-Gerät, dessen Antwort beim Autor stumm bleibt — also den, der
 * am wenigsten dagegen tun kann.
 */

import { nachWav } from './tonwandeln'

/** Die Formate in der Reihenfolge, in der wir sie haben wollen. */
const FORMATE = [
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
]

export interface Aufnahme {
  blob: Blob
  dauerMs: number
  /** Ob das Format auf jedem Gerät läuft — sonst steht ein Hinweis dabei. */
  ueberall: boolean
}

/** Ob dieser Browser überhaupt aufnehmen kann. */
export function kannAufnehmen(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  )
}

/** Das beste Format, das dieser Browser aufnimmt — oder `null`. */
function bestesFormat(): string | null {
  for (const format of FORMATE) {
    if (MediaRecorder.isTypeSupported(format)) return format
  }
  return null
}

/**
 * Eine laufende Aufnahme.
 *
 * `stoppen()` gibt die fertige Aufnahme zurück, `abbrechen()` wirft sie weg —
 * und **beide geben das Mikrofon frei**. Ein Aufnahmepunkt, der nach dem
 * Abbrechen weiterleuchtet, ist das, was Vertrauen kostet.
 */
export interface LaufendeAufnahme {
  stoppen: () => Promise<Aufnahme>
  abbrechen: () => void
  /** Anhalten und weitermachen — beides nur, wenn `kannPausieren` gilt. */
  pausieren: () => void
  weiter: () => void
  /**
   * Ob dieser Browser überhaupt anhalten kann.
   *
   * **Kann er es nicht, steht der Knopf gar nicht erst da.** Eine Taste, die
   * nichts tut, ist schlimmer als eine fehlende — das ist die Regel „keine
   * stille Absage" in der einfachsten Form, die es gibt.
   */
  kannPausieren: boolean
  /**
   * Die aufgenommene Zeit in Millisekunden — **ohne die Pausen.**
   *
   * Hier stand `Date.now() - beginn`, und mit einer Pause meldete das zu
   * viel: die Zahl unter der Wellenform hätte weitergezählt, während nichts
   * aufgenommen wurde, und am Ende stünde an der Sprechblase eine Länge, die
   * das Abspielen nie erreicht.
   */
  dauerJetzt: () => number
  /**
   * Wie laut es gerade ist, zwischen 0 und 1 — für die Wellenform.
   *
   * **Fehlt die Messung, ist es 0 und sonst nichts.** Ein `AudioContext` kann
   * auf einem Gerät verweigert werden; dann steht eine flache Linie da und die
   * Aufnahme läuft weiter. Nichts daran ist fatal.
   */
  pegel: () => number
}

/**
 * Aufnehmen — und dabei den Pegel messen.
 *
 * **Der `AudioContext` gehört in den Tipp, der die Aufnahme startet.** Safari
 * lässt ihn nur in einer Benutzergeste anlaufen, und nach dem `await` auf
 * `getUserMedia` ist die Geste vorbei. Wer ihn übergibt, bekommt deshalb eine
 * Wellenform, die sich auch auf einem iPhone bewegt; wer nicht, bekommt eine
 * flache — und in beiden Fällen dieselbe Aufnahme.
 */
export async function starteAufnahme(kontext?: AudioContext): Promise<LaufendeAufnahme> {
  const spur = await navigator.mediaDevices.getUserMedia({ audio: true })
  const format = bestesFormat()
  const recorder = new MediaRecorder(spur, format === null ? undefined : { mimeType: format })
  const stuecke: Blob[] = []

  /*
   * **Aufsummiert statt abgezogen.** `summe` trägt, was vor der laufenden
   * Strecke schon aufgenommen war; `seit` ist der Beginn der laufenden. Eine
   * Pause schiebt das eine ins andere.
   */
  let summe = 0
  let seit: number | null = Date.now()
  const dauerJetzt = () => summe + (seit === null ? 0 : Date.now() - seit)

  recorder.ondataavailable = (ereignis) => {
    if (ereignis.data.size > 0) stuecke.push(ereignis.data)
  }
  recorder.start()

  // **Der Pegel ist Beiwerk und wird auch so behandelt.** Scheitert er,
  // scheitert nicht die Aufnahme.
  let messer: AnalyserNode | null = null
  /* **Mit `ArrayBuffer` ausgeschrieben.** Seit TypeScript 5.7 ist
     `Uint8Array` über seinen Speicher generisch, und `getByteTimeDomainData`
     nimmt keinen geteilten. Ohne die Angabe rät der Übersetzer `ArrayBufferLike`
     und lehnt den eigenen Puffer ab. */
  let daten: Uint8Array<ArrayBuffer> | null = null
  let eigenerKontext: AudioContext | null = null
  try {
    const ton = kontext ?? new AudioContext()
    if (kontext === undefined) eigenerKontext = ton
    void ton.resume()
    messer = ton.createAnalyser()
    messer.fftSize = 1024
    ton.createMediaStreamSource(spur).connect(messer)
    daten = new Uint8Array(new ArrayBuffer(messer.fftSize))
  } catch {
    messer = null
  }

  /**
   * Der Ausschlag als eine Zahl.
   *
   * **Der quadratische Mittelwert und nicht der Höchstwert:** ein einzelner
   * Knacks risse den Balken sonst bis oben, und die Wellenform zeigte
   * Störungen statt Stimme. Der Faktor bringt normales Sprechen in die obere
   * Hälfte — gemessen liegt es sonst bei rund 0,1 und sähe aus, als käme
   * nichts an.
   */
  const pegel = () => {
    if (messer === null || daten === null) return 0
    messer.getByteTimeDomainData(daten)
    let summeQuadrate = 0
    for (const wert of daten) {
      const abstand = (wert - 128) / 128
      summeQuadrate += abstand * abstand
    }
    return Math.min(1, Math.sqrt(summeQuadrate / daten.length) * 4)
  }

  const freigeben = () => {
    spur.getTracks().forEach((t) => t.stop())
    // Nur den eigenen schließen: einer, den der Aufrufer hält, gehört ihm.
    void eigenerKontext?.close()
  }

  return {
    kannPausieren: typeof recorder.pause === 'function',
    dauerJetzt,
    pegel,
    pausieren: () => {
      if (recorder.state !== 'recording') return
      summe = dauerJetzt()
      seit = null
      recorder.pause()
    },
    weiter: () => {
      if (recorder.state !== 'paused') return
      seit = Date.now()
      recorder.resume()
    },
    stoppen: () =>
      new Promise<Aufnahme>((fertig) => {
        const gemessen = dauerJetzt()
        recorder.onstop = () => {
          freigeben()
          const typ = recorder.mimeType || format || 'audio/webm'
          fertig({
            blob: new Blob(stuecke, { type: typ }),
            dauerMs: gemessen,
            ueberall: typ.startsWith('audio/mp4'),
          })
        }
        recorder.stop()
      }),
    abbrechen: () => {
      recorder.onstop = freigeben
      if (recorder.state !== 'inactive') recorder.stop()
      else freigeben()
    },
  }
}

/**
 * Aus einer Aufnahme eine machen, die überall läuft.
 *
 * Wo schon MP4 herauskam, passiert **nichts**: nicht dekodieren, nicht neu
 * rechnen, nicht neu verpacken. Das ist der Normalfall, und er kostet
 * dadurch weder Zeit noch Qualität noch Bytes.
 *
 * **Scheitert das Umwandeln, geht die Aufnahme trotzdem raus** — mit dem
 * alten Hinweis. Eine gesprochene Nachricht wegzuwerfen, weil eine
 * Umrechnung nicht ging, wäre die schlechtere Anwendung.
 */
export async function ueberallAbspielbar(roh: Aufnahme): Promise<Aufnahme> {
  if (roh.ueberall) return roh

  try {
    const blob = await nachWav(roh.blob)
    return { blob, dauerMs: roh.dauerMs, ueberall: true }
  } catch {
    return roh
  }
}

/** Aus Millisekunden „0:07" — für die Blase und den laufenden Zähler. */
export function dauer(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Die Decke für eine Aufnahme, solange die Einstellungen noch nicht da sind.
 *
 * **Der Client deckelt die Länge, der Server die Bytes** — die Dauer ließe
 * sich dort ohne ffmpeg nicht nachprüfen, und eine Zahl, die man glauben
 * muss, ist keine Regel. Zwei Minuten waren hier eine Konstante; seit 1.9.0
 * steht die Zahl in den Einstellungen (`voice_max_seconds`, Administration ›
 * Größe von Medien), und dies ist nur noch die Vorgabe für den Augenblick,
 * bevor sie geladen sind.
 */
export const HOECHSTENS_MS = 120_000
